import React, { useState, useEffect, useRef } from 'react';
import '../styled/pages/dashboard.scss';
import { useNavigate, useLocation } from 'react-router-dom';
import { Icon } from '@ohif/ui';
import {
  getInitials,
  capitalizeFirstLetterOfEachWord,
  formatTimeInAMPM,
  formatDateToDDMMYYYY,
} from '../Utils/utils';
import AssignDoctorModal from '../pages/patients/components/assignToModal';
import { STATUS_LABELS } from '../pages/express/DistributionPage';
import { getName, getRole, getUserId } from '../Utils/authUtils';
import { isDoctorRole } from '../Utils/reportPermissions';

type Column = {
  key: string;
  title: string;
};

type Action = {
  label: string;
  iconName?: string;
  dropdownItems?: any[];
  onClick: (rowData: any) => void;
};

type Props = {
  columns: Column[];
  tableData: any[];
  actions?: Action[];
  from?: string;
  setOnAssigningReport?: (value: boolean) => void;
};

const CustomTable = ({ columns, tableData, actions, from, setOnAssigningReport }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedRows, setSelectedRows] = useState<boolean[]>(Array(tableData?.length).fill(false));
  const [selectAll, setSelectAll] = useState(false);
  const [activeIndex, setActiveIndex] = useState<string | null>(null); // Track active action for each row
  const [isAssignDoctorModalOpen, setAssignDoctorModalOpen] = useState(false);
  const [ReportId, setReportId] = useState('');
  const [AssignReportStatus, setAssignReportStatus] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as Element).closest('.dropdownMenu')) {
        setActiveIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Toggle popup visibility for a specific row and action
  const handleMoreIconClick = (event: React.MouseEvent, actionIndex: number, rowIndex: number) => {
    event.stopPropagation(); // Prevent event bubbling
    const uniqueKey = `${rowIndex}-${actionIndex}`; // Create a unique key based on row and action
    setActiveIndex(prevIndex => (prevIndex === uniqueKey ? null : uniqueKey)); // Toggle the specific popup
  };

  // Handle Select All toggle
  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setSelectedRows(Array(tableData?.length).fill(newSelectAll));
  };

  // Handle individual row selection
  const handleRowSelect = (rowIndex: number) => {
    const newSelectedRows = [...selectedRows];
    newSelectedRows[rowIndex] = !newSelectedRows[rowIndex];
    setSelectedRows(newSelectedRows);
    setSelectAll(newSelectedRows.every(isSelected => isSelected));
  };

  const handleAssignClick = (reportId, status) => {
    console.log(isAssignDoctorModalOpen, 'jhfkjshkjfhskjf');
    setAssignReportStatus(status);
    setReportId(reportId);
    setAssignDoctorModalOpen(true);
  };

  const role = getRole();

  const handleNameClick = data => {
    if (from == 'reports') {
      // Receptionist/Practice Manager don't have Express access — go to View Report
      if (role === 'Medical Receptionist' || role === 'Practice Manager') {
        navigate(`/patientReports/${data?._id}`, {
          state: { reportId: data?._id },
        });
        return;
      }
      navigateToExpressWithReports(data);
      return;
    }
    if (location.pathname.includes('/reports')) {
      navigate(`/patientReports/${data?._id}`, {
        state: {
          reportId: data?._id,
        },
      });
    }
    if (location.pathname.includes('/patientReports')) {
      window.location.href = `/patientReports/${data?._id}`;
    }
  };

  const handleFileUpload = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'application/pdf';
    fileInput.onchange = event => {
      const file = event.target.files[0];
      if (file) {
        setUploadedFileName(file.name);
        console.log('Uploaded file:', file);
      }
    };
    fileInput.click();
  };

  const handleAssignmentUpdate = (value: boolean) => {
    if (setOnAssigningReport) {
      setOnAssigningReport(value);
    }
  };

  const navigateToExpressWithReports = item => {
    navigate(`/express/${item?.studyInstanceUID}`, {
      state: {
        reportId: item?._id,
        patientName: item?.name,
        modalities: item?.modality,
        mrn: item?.patientId,
        date: item?.date,
        time: item?.time,
        instances: item?.numberOfImages,
        studyInstanceUID: item?.studyInstanceUID,
        ...item,
      },
    });
  };

  return (
    <div className="tableContainer">
      <table className="table">
        <thead>
          <tr>
            {columns?.map(column => (
              <th key={column.key}>
                <div className="cellText">{column.title}</div>
              </th>
            ))}
            {/* Render the "Actions" header if actions are provided */}
            {actions && (
              <th>
                <div className="actions">Action</div>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {tableData?.length > 0 ? (
            tableData?.map((rowData, rowIndex) => {
              return (
                <tr key={rowIndex}>
                  {columns?.map((column, colIndex) => {
                    return (
                      <td key={colIndex}>
                        {column.key === 'name' ? (
                          <div
                            className="profile"
                            onClick={() => handleNameClick(rowData)}
                            style={{ cursor: 'pointer', position: 'relative' }}
                          >
                            <div className="profile__initials">
                              {getInitials(rowData[column.key])}
                            </div>
                            <span className="profile__name">
                              {capitalizeFirstLetterOfEachWord(rowData[column.key])}
                            </span>
                            {rowData.pendingReview && (
                              <span
                                style={{
                                  marginLeft: '6px',
                                  fontSize: '14px',
                                  color: '#faad14',
                                  animation: 'bellPulse 1.5s ease-in-out infinite',
                                }}
                                title={`Reminded — ${rowData.reminderCount || 1} time(s)`}
                              >
                                🔔
                              </span>
                            )}
                          </div>
                        ) : column.key === 'status' ? (
                          <div className="actions">
                            <span className="btn btn__success">{rowData[column.key]}</span>
                          </div>
                        ) : column.key === 'role' ? (
                          <div className="actions">
                            <span className="btn btn__yellow">{rowData[column.key]}</span>
                          </div>
                        ) : column.key === 'adminName' ? (
                          <div className="actions">
                            <span className="btn btn__yellow">{rowData[column.key]}</span>
                          </div>
                        ) : column.key === 'port' ? (
                          <div className="actions">
                            <span className="btn btn__yellow">{rowData[column.key]}</span>
                          </div>
                        ) : column.key === 'file' ? (
                          <button
                            className="assign-button"
                            onClick={handleFileUpload}
                          >
                            <div className="cellText">
                              <span className="cellText__primary cellText--medium">
                                {/* {uploadedFileName && <Icon name="pdf-file" />} */}+ Add File
                              </span>
                            </div>
                          </button>
                        ) : column.key === 'assignTo' ? (
                          <div className="cellText cellText--flex">
                            {role === 'Admin' ||
                            role === 'Sonographer' ||
                            role === 'Medical Receptionist' ? (
                              !rowData['reportAssignedTo'] ? (
                                <button
                                  className="assign-button"
                                  onClick={() => handleAssignClick(rowData?._id, 'Assign')}
                                >
                                  <div className="cellText">
                                    <span className="cellText__primary cellText--medium cellText--underline">
                                      + Assign
                                    </span>
                                  </div>
                                </button>
                              ) : (
                                <>
                                  <span className="cellText__Dark" title={rowData['reportAssignedToEmail'] || ''}>
                                    {capitalizeFirstLetterOfEachWord(rowData['reportAssignedTo'])}{' '}
                                    {capitalizeFirstLetterOfEachWord(rowData[column.key])}
                                  </span>
                                  {rowData['status'] !== 'Finalized' && (
                                    <div className="actions">
                                      <button
                                        className="btn btn__yellow"
                                        onClick={() => handleAssignClick(rowData?._id, 'Reassign')}
                                      >
                                        <div className="flex gap-1">Update</div>
                                      </button>
                                    </div>
                                  )}
                                </>
                              )
                            ) : rowData['reportAssignedTo'] ? (
                              <span className="cellText__Dark" title={rowData['reportAssignedToEmail'] || ''}>{rowData['reportAssignedTo']}</span>
                            ) : (
                              <span className="cellText__Dark">{'--'}</span>
                            )}
                          </div>
                        ) : column.key === 'date' ? (
                          <div className="cellText">
                            <span className="cellText__dark">
                              {`${formatDateToDDMMYYYY(rowData?.date)}, ${formatTimeInAMPM(rowData?.time)}`}
                            </span>
                          </div>
                        ) : column.key === '_distLabel' ? (
                          (() => {
                            const info = rowData._distData;
                            if (!info || info.count === 0) {
                              return (
                                <div className="cellText">
                                  <span className="cellText__dark">{info ? 'No distributions' : '-'}</span>
                                </div>
                              );
                            }
                            const formatStatus = (s: string) => STATUS_LABELS[s] || s;
                            const formatDate = (iso: string | undefined) => {
                              if (!iso) return '';
                              const d = new Date(iso);
                              return isNaN(d.getTime())
                                ? ''
                                : `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                            };
                            const first = info.distributions[0];
                            const label =
                              info.count === 1
                                ? `1 — ${formatStatus(first?.status)}`
                                : `${info.count} distributions`;
                            const tooltip = info.distributions
                              .map((d, i) => {
                                const parts = [
                                  `${i + 1}.`,
                                  d.toProviderName || 'Unknown',
                                  '—',
                                  formatStatus(d.status),
                                ];
                                const date = formatDate(d.createdAt);
                                if (date) parts.push('—', date);
                                return parts.join(' ');
                              })
                              .join('\n');
                            return (
                              <div
                                className="cellText"
                                style={{ cursor: 'pointer' }}
                                title={tooltip}
                                onClick={e => {
                                  e.stopPropagation();
                                  if (rowData.studyInstanceUID) {
                                    navigate(`/distribution/${rowData.studyInstanceUID}`, {
                                      state: {
                                        reportId: rowData._id,
                                        studyData: {
                                          name: rowData.name,
                                          patientName: rowData.name,
                                          dob: rowData.dob,
                                          sex: rowData.sex,
                                          age: rowData.age,
                                          patientId: rowData.patientId,
                                          reportName: rowData.reportName,
                                          studyInstanceUID: rowData.studyInstanceUID,
                                        },
                                      },
                                    });
                                  }
                                }}
                              >
                                <span className="cellText__dark" style={{ color: '#4f8ef7' }}>{label}</span>
                              </div>
                            );
                          })()
                        ) : (
                          <div className="cellText">
                            <span className="cellText__dark">{rowData[column.key]}</span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                  {/* Render the action buttons if actions exist */}
                  {actions && (
                    <td>
                      <div className="actions">
                        {actions?.map((action, actionIndex) => {
                          const loggedInUserName = getName();
                          const loggedInUserId = getUserId();
                          const reportAssignedToId = rowData['reportAssignedToId'] || '';
                          const reportAssignedTo = rowData['reportAssignedTo'] || '';
                          const idMatch = reportAssignedToId && loggedInUserId && String(loggedInUserId) === String(reportAssignedToId);
                          const nameMatch = reportAssignedTo && loggedInUserName && loggedInUserName.toLowerCase() === reportAssignedTo.toLowerCase();
                          const isAssigned = !!(idMatch || nameMatch);
                          const isDoctor = isDoctorRole(role);

                          // View-only items always shown; status-change items only for assigned doctor
                          const viewOnlyIds = ['view-report', 'view-report-with-studies', 'distribute'];
                          const filteredDropdownItems =
                            isDoctor && !isAssigned
                              ? action.dropdownItems?.filter(item => viewOnlyIds.includes(item.id))
                              : action.dropdownItems;

                          return (
                            <li
                              className="dropdown__item"
                              key={actionIndex}
                            >
                              <button
                                className="btn p_0 border_0"
                                onClick={event => {
                                  if (action.iconName === 'more-icon') {
                                    handleMoreIconClick(event, actionIndex, rowIndex); // Toggle popup for this specific action
                                  } else {
                                    action.onClick(rowData); // Execute other actions normally
                                  }
                                }}
                                title={action.label}
                              >
                                {from === 'auditLogs' ? (
                                  // <p className="actions__link">{action.label}</p>
                                  <div className="actions">
                                    <button className="link link__primary">View Details</button>
                                  </div>
                                ) : (
                                  <Icon name={action.iconName} />
                                )}
                              </button>
                              <ul className="dropdown">
                                {activeIndex === `${rowIndex}-${actionIndex}` &&
                                  filteredDropdownItems && (
                                    <ul className="dropdownMenu dropdownMenu--open">
                                      {filteredDropdownItems?.map((dropdownItem, index) => {
                                        // Allow send-reminder to pass through for role-based visibility check
                                        if (dropdownItem.id === 'send-reminder' || dropdownItem.id === 'send-notification') {
                                          // fall through to isVisible check below
                                        } else if (
                                          (role === 'Admin' ||
                                            role === 'Sonographer' ||
                                            role === 'Medical Receptionist' ||
                                            role === 'Practice Manager') &&
                                          dropdownItem.id !== 'view-report' &&
                                          dropdownItem.id !== 'view-report-with-studies' &&
                                          dropdownItem.id !== 'distribute' &&
                                          dropdownItem.id !== 'delete-report' &&
                                          !(role === 'Sonographer' && (dropdownItem.id === 'Draft' || dropdownItem.id === 'Preliminary'))
                                        ) {
                                          return null;
                                        }

                                        {
                                          // Support isVisible callback for conditional dropdown items
                                          if (typeof dropdownItem.isVisible === 'function' && !dropdownItem.isVisible(rowData)) {
                                            return null;
                                          }

                                          const isFinalizedReport = from === 'reports' && rowData['status'] === 'Finalized';
                                          const isStatusAction = !viewOnlyIds.includes(dropdownItem.id);
                                          const finalizedById = rowData['finalizedById'] || '';
                                          const canUnfinalize = finalizedById && loggedInUserId === finalizedById;

                                          // Hide current status from dropdown
                                          if (from === 'reports' && rowData['status'] === dropdownItem.id) {
                                            return null;
                                          }

                                          // For finalized reports: only the doctor who finalized can change status
                                          if (isFinalizedReport && isStatusAction) {
                                            if (!canUnfinalize) return null;
                                          }

                                          return (
                                            <li
                                              className="dropdown__item"
                                              onClick={() => dropdownItem.onClick(rowData)}
                                              key={index}
                                            >
                                              <span className="icon">
                                                <Icon name={dropdownItem.iconName} />
                                              </span>
                                              {dropdownItem.label}
                                            </li>
                                          );
                                        }
                                      })}
                                    </ul>
                                  )}
                              </ul>
                            </li>
                          );
                        })}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={columns.length + (actions ? 1 : 0)}>
                <div className="flex h-40 items-center justify-center">
                  <p className="text-lg font-semibold text-gray-600">No Result Found!</p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <AssignDoctorModal
        onClose={() => setAssignDoctorModalOpen(false)}
        reportId={ReportId}
        setOnAssigningReport={handleAssignmentUpdate}
        AssignReportStatus={AssignReportStatus}
        isAssignDoctorModalOpen={isAssignDoctorModalOpen}
      />
    </div>
  );
};

export default CustomTable;
